import { delay } from 'redux-saga';
import {
  all,
  call,
  put,
  race,
  select,
  spawn,
  take,
  takeLatest,
} from 'redux-saga/effects';

import { STOCK_STATUSES, STOCK_TYPES, MATERIAL_PROVIDERS } from 'constants/all';

import { MATERIAL_INDICATOR } from 'modules/common/viewer/cadViewer/constants';
import {
  types as CADTypes,
  cadViewer,
  materialStock as CADMaterialStock,
} from 'modules/common/viewer/cadViewer/data/actions';
import { getViewerIdsForPartFileUuid } from 'modules/common/viewer/cadViewer/data/partFileCadData/reducer';
import {
  getFocusedBodies,
  getVisibleBodies,
} from 'modules/common/viewer/cadViewer/data/reducer';
import { partFileStatuses } from 'modules/viewerPage/data/actions';
import { APPS_PANEL_STATES } from 'modules/viewerPage/data/constants';
// TODO: decouple this selector call from the viewer page reducer
import {
  getPartUuidFromPartFileUuid,
  getAppsPanelState,
} from 'modules/viewerPage/data/reducer';

import {
  materialWidget as materialWidgetService,
  recordEvent as recordEventService,
} from 'services/materialService';

import { PAGE_REQUEST } from 'store/app/actions';
import { getHasFetched, getIsFetching } from 'store/materials/reducer';
import { initMaterialStore } from 'store/materials/sagas';

import { barWidget } from '../bar/data/actions';
import { rodWidget } from '../rod/data/actions';
import { sheetWidget } from '../sheet/data/actions';
import { convertStockResultToMM } from '../utils';
import {
  types,
  materialWidget,
  stockInterrogatingList,
  visualize,
} from './actions';
import {
  getActivePartFileStatus,
  getPartFilePacket,
  getPartFileStatuses,
  getPartFileUuid,
  getPartUuid,
  getPreviousCADViewerKeys,
  getStockInterrogatingList,
  getSuccessfullyVisualizedKeys,
  getVisualizedResultKey,
} from './reducer';

export default function* root() {
  yield takeLatest(types.MATERIAL_WIDGET_INIT_REQUEST, watchInitMaterialWidget);
  yield takeLatest(
    types.MATERIAL_WIDGET_TURN_OFF_VISUALIZATION_IF_NECESSARY,
    watchTurnOffVisualization,
  );
  yield takeLatest(types.VISUALIZE_SHOW_REQUEST, watchShowRequest);
  yield takeLatest(
    [types.VISUALIZE_HIDE_REQUEST, types.SET_ACTIVE_TAB],
    watchHideRequest,
  );
  yield takeLatest(types.MATERIAL_WIDGET_RESET, watchReset);
  yield takeLatest(types.MATERIAL_WIDGET_RESET_CHILDREN, watchResetChildren);
  yield takeLatest(types.INTERROGATION_REQUEST, watchInterrogationRequest);
  yield takeLatest(
    types.RECORD_NAVIGATE_TO_VENDOR_EVENT,
    watchNavigateToVendorEvent,
  );
  yield takeLatest(
    types.MATERIAL_WIDGET_RECEIVE_SELECTED_MATERIAL_ID,
    watchReceiveSelectedMaterialId,
  );
  yield spawn(pageWorker);
}

function* pageWorker() {
  // restart "periodicallyCheckStockInterrogationStatuses" saga when
  // PAGE_REQUEST is dispatched
  for (;;) {
    yield race({
      materialWidget: call(periodicallyCheckStockInterrogationStatuses),
      otherPage: take(PAGE_REQUEST),
    });
  }
}

function* periodicallyCheckStockInterrogationStatuses() {
  yield take(types.MATERIAL_WIDGET_RESET);
  let response;
  while (true) {
    try {
      const stockInterrogatingListUuids = yield select(
        getStockInterrogatingList,
      );
      if (stockInterrogatingListUuids.length) {
        response = yield call(
          materialWidgetService.stockStatusCheck,
          stockInterrogatingListUuids,
        );

        const oldStatuses = yield select(getPartFileStatuses);
        const oldStatusKeys = Object.keys(oldStatuses);
        let areAnyDifferent = false;
        const newStatuses = {};
        for (const status of response.statuses) {
          newStatuses[status.uuid] = { ...status };

          // determine if new status is different than the old status
          if (!oldStatusKeys.includes(status.uuid)) {
            areAnyDifferent = true;
          } else {
            const oldStatus = oldStatuses[status.uuid];
            if (
              Object.keys(oldStatus).length !==
              Object.keys(status).length - 1
            ) {
              areAnyDifferent = true;
            } else {
              for (const subKey in oldStatus) {
                if (oldStatus[subKey] != status[subKey]) {
                  areAnyDifferent = true;
                  break;
                }
              }
            }
          }

          // determine if we no longer need to poll for this uuid
          let allNotInterrogating = true;
          let shouldRefetch = false;
          for (const stockType of Object.values(STOCK_TYPES)) {
            if (status[stockType] === STOCK_STATUSES.INTERROGATING) {
              allNotInterrogating = false;
            } else if (
              [STOCK_STATUSES.SUCCESSFUL, STOCK_STATUSES.FAILED].includes(
                status[stockType],
              )
            ) {
              shouldRefetch = true;
            }
          }
          if (allNotInterrogating) {
            yield put(stockInterrogatingList.remove({ uuid: status.uuid }));
          }
          if (shouldRefetch) {
            // TODO: remove this call to viewer page reducer
            // Update local store to fetch part uuid or have part master version present or something
            const partUuid = yield select(
              getPartUuidFromPartFileUuid,
              status.uuid,
            );
            const newPacket = yield call(
              materialWidgetService.getPartFilePacket,
              status.uuid,
              { partUuid },
            );
            yield put(
              materialWidget.receivePartFilePacket({
                partFilePacket: newPacket,
              }),
            );

            yield put(
              sheetWidget.onUpdatePartFilePacket({ partFilePacket: newPacket }),
            );
          }
        }

        // only receive the statuses if anything is different
        if (areAnyDifferent) {
          yield put(
            materialWidget.receiveStatuses({ partFileStatuses: newStatuses }),
          );
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      yield call(delay, 1500);
    }
  }
}

function* watchReset() {
  yield all([
    put(barWidget.reset()),
    put(rodWidget.reset()),
    put(sheetWidget.reset()),
  ]);
}

function* watchResetChildren() {
  yield all([
    put(barWidget.reset()),
    put(rodWidget.reset()),
    put(sheetWidget.reset()),
  ]);
}

function* watchInitMaterialWidget(action) {
  const { payload } = action;
  const { partFileUuid, partUuid } = payload;

  // check to see if material store has been initialized
  const isMaterialStoreReady = yield select(getHasFetched);
  const isMaterialStoreFetching = yield select(getIsFetching);
  if (!isMaterialStoreReady || isMaterialStoreFetching) {
    if (!isMaterialStoreFetching) {
      yield call(initMaterialStore);
    }
  }

  try {
    const response = yield call(
      materialWidgetService.getPartFilePacket,
      partFileUuid,
      { partUuid },
    );

    const { materialId, status } = response;
    let hasAnyInterrogating = false;
    if (status) {
      for (const key in status) {
        if (
          ![STOCK_STATUSES.SUCCESSFUL, STOCK_STATUSES.FAILED].includes(
            status[key],
          )
        ) {
          hasAnyInterrogating = true;
          break;
        }
      }
      yield put(materialWidget.receiveStatuses({ [partFileUuid]: status }));
    }
    if (hasAnyInterrogating) {
      yield put(stockInterrogatingList.add({ uuid: partFileUuid }));
    }

    yield all([
      put(barWidget.reset()),
      put(rodWidget.reset()),
      put(sheetWidget.receiveNestingResult({ nestingResult: null })),
      put(materialWidget.receiveSelectedMaterialId({ materialId })),
      put(materialWidget.receivePartFilePacket({ partFilePacket: response })),
      put(materialWidget.initSuccess()),
    ]);
  } catch (error) {
    yield put(materialWidget.initError(error));
  }
}

function* watchReceiveSelectedMaterialId(action) {
  const { payload } = action;
  const { materialId } = payload;

  if (materialId) {
    yield put(materialWidget.checkAvailabilityStart());
    const response = yield call(
      materialWidgetService.stockAvailability,
      materialId,
    );
    yield put(
      materialWidget.checkAvailabilityFinish({
        materialAvailability: response,
      }),
    );
  } else {
    const availability = {};
    Object.keys(MATERIAL_PROVIDERS).forEach((key) => {
      availability[key] = { bar: false, rod: false, sheet: false };
    });
    yield put(
      materialWidget.checkAvailabilityFinish({
        materialAvailability: availability,
      }),
    );
  }
}

function* watchHideRequest() {
  yield all([
    put(visualize.receiveVisualizedKey({ resultKey: null })),
    put(CADMaterialStock.receiveVisibleResultKeys({ resultKeys: [] })),
  ]);

  const { focused: focusedKeys, visible: visibleKeys } = yield select(
    getPreviousCADViewerKeys,
  );
  if (focusedKeys) {
    yield put(
      cadViewer.receiveFocusedBodies({
        focusedBodies: focusedKeys.filter(
          (id) => id.indexOf(MATERIAL_INDICATOR) === -1,
        ),
      }),
    );
  }
  if (visibleKeys) {
    yield put(
      cadViewer.receiveVisibleBodies({
        visibleBodies: visibleKeys.filter(
          (id) => id.indexOf(MATERIAL_INDICATOR) === -1,
        ),
      }),
    );
  }
}

function* watchTurnOffVisualization() {
  const visualizedResultKey = yield select(getVisualizedResultKey);
  const currentVisibleBodies = yield select(getVisibleBodies);
  const appsPanelState = yield select(getAppsPanelState);
  if (
    appsPanelState !== APPS_PANEL_STATES.ONLINE_METALS_MATERIAL_WIDGET ||
    (visualizedResultKey &&
      (!currentVisibleBodies.length ||
        currentVisibleBodies.includes(visualizedResultKey)))
  ) {
    //Then user is toggling nested result visibility
    yield call(watchHideRequest);
  } //otherwise the user changed visibility via BOM tree and the barWidget, rodWidget and sheetWidget will be reset
}

export function* getViewerIdForStockResultUuid(uuid) {
  // focus on body from current file focus, if there are multiple instances of this body and
  // one is currently focused, focus on that particular one
  try {
    const partFileViewerIds = yield select(getViewerIdsForPartFileUuid, uuid);
    const currentFocusedBodies = yield select(getFocusedBodies);
    let focusViewerId = partFileViewerIds[0];
    for (const potentialFocusViewerId of partFileViewerIds) {
      if (currentFocusedBodies.includes(potentialFocusViewerId)) {
        focusViewerId = potentialFocusViewerId;
        break;
      }
    }

    return focusViewerId;
  } catch (error) {
    console.error('There was an error in getViewerIdForStockResultUuid');
    throw error;
  }
}

function* setFocusVisibleForStockResult(stockResult, isLoading) {
  const resultKey = stockResult.key;

  yield put(
    CADMaterialStock.receiveVisibleResultKeys({ resultKeys: [resultKey] }),
  );

  const focusViewerId = yield call(
    getViewerIdForStockResultUuid,
    stockResult.uuid,
  );

  if (stockResult.stockType === STOCK_TYPES.SHEET) {
    // for sheet, turn off all bodies (focused and visible)
    if (!isLoading) {
      yield all([
        put(cadViewer.receiveFocusedBodies({ focusedBodies: [resultKey] })),
        put(cadViewer.receiveVisibleBodies({ visibleBodies: [resultKey] })),
      ]);
    }
  } else {
    // turn of all except for the first body associated with the stock and the result
    const nextFocused = focusViewerId
      ? [focusViewerId, resultKey]
      : [resultKey];
    yield put(cadViewer.receiveFocusedBodies({ focusedBodies: nextFocused }));
  }

  const filterPred = (key) => key.indexOf(MATERIAL_INDICATOR) === -1;
  const previousFocusedBodies = (yield select(getFocusedBodies)).filter(
    filterPred,
  );
  if (!previousFocusedBodies.includes(focusViewerId)) {
    previousFocusedBodies.push(focusViewerId);
  }
  const previousVisibleBodies = (yield select(getVisibleBodies)).filter(
    filterPred,
  );
  if (!previousVisibleBodies.includes(focusViewerId)) {
    previousVisibleBodies.push(focusViewerId);
  }
  yield put(
    materialWidget.receivePreviousCADViewerKeys({
      previousCADViewerKeys: {
        focused: previousFocusedBodies,
        visible: previousVisibleBodies,
      },
    }),
  );
}

function* watchShowRequest(action) {
  const { payload } = action;
  const { result: stockResult } = payload;
  try {
    const resultKey = stockResult.key;

    const successfullyVisualizedKeys = yield select(
      getSuccessfullyVisualizedKeys,
    );

    if (successfullyVisualizedKeys.includes(resultKey)) {
      yield all([
        put(visualize.receiveVisualizedKey({ resultKey })),
        put(visualize.showSuccess()),
      ]);

      yield call(setFocusVisibleForStockResult, stockResult, false);
    } else {
      // prepare result object and convert it
      const packet = yield select(getPartFilePacket);

      if (stockResult.stockType === STOCK_TYPES.SHEET) {
        let tessellation = null,
          transformsInfo = null;
        if (
          stockResult.nestingJson &&
          !stockResult.useBaseGeometry &&
          !stockResult.isManual
        ) {
          tessellation = yield fetch(stockResult.nestingJson).then((response) =>
            response.json(),
          );
        }
        if (stockResult.transformsUrl) {
          transformsInfo = yield fetch(stockResult.transformsUrl).then(
            (response) => response.json(),
          );
        }
        // for sheet, turn off all bodies and other stock results to help
        // with continuity and render performance
        yield put(
          CADMaterialStock.receiveVisibleResultKeys({ resultKeys: [] }),
        );
        yield put(cadViewer.receiveVisibleBodies({ visibleBodies: [] }));
        yield put(
          CADMaterialStock.receiveNewResult({
            result: {
              ...stockResult,
              tessellation,
              transformsInfo,
            },
          }),
        );
      } else {
        yield put(
          CADMaterialStock.receiveNewResult({
            result: {
              ...packet.stockInformation[stockResult.stockType],
              ...convertStockResultToMM(stockResult),
            },
          }),
        );
      }

      const winner = yield race({
        success: take(CADTypes.MATERIAL_STOCK_CREATE_SUCCESS),
        failure: take(CADTypes.MATERIAL_STOCK_CREATE_FAILED),
      });

      // todo: only take success or failure if key in payload matches the result key
      if (winner.success && winner.success.payload.resultKey === resultKey) {
        yield all([
          put(visualize.receiveVisualizedKey({ resultKey })),
          put(visualize.receiveNewSuccesfullyVisualizedKey({ resultKey })),
          put(visualize.showSuccess()),
        ]);

        // for sheet types, we will acknowledge and do display action separately
        // as these need to load in
        if (stockResult.stockType === STOCK_TYPES.SHEET) {
          yield call(setFocusVisibleForStockResult, stockResult, true);
        } else {
          yield call(setFocusVisibleForStockResult, stockResult, false);
        }
      } else if (
        winner.failure &&
        winner.failure.payload.resultKey === resultKey
      ) {
        yield put(visualize.showError('Failed to render result'));
      } else {
        yield put(visualize.showError('Stock visualization request timed out'));
      }
    }
  } catch (error) {
    yield put(visualize.showError(error));
  }
}

function* watchInterrogationRequest(action) {
  const { payload } = action;
  const { stockTask, customInterrogationId } = payload;
  const activeUuid = yield select(getPartFileUuid);
  const activePartUuid = yield select(getPartUuid);

  try {
    const response = yield call(
      materialWidgetService.stockInterrogation,
      activeUuid,
      {
        stockTask,
        customInterrogationId,
        partUuid: activePartUuid,
      },
    );

    // if it is sheet stock task, we need to add to pending uuids for geometric interrogation
    if (stockTask === STOCK_TYPES.SHEET) {
      const nextInterrogationStatuses = {
        [activeUuid]: response,
      };
      yield put(
        partFileStatuses.receiveInterrogationStatuses({
          interrogationStatuses: nextInterrogationStatuses,
        }),
      );
    }

    const existingStatus = yield select(getActivePartFileStatus);
    const fakeStatus = {
      [activeUuid]: {
        ...existingStatus,
        [stockTask]: STOCK_STATUSES.INTERROGATING,
      },
    };
    yield put(materialWidget.receiveStatuses({ partFileStatuses: fakeStatus }));
    yield put(stockInterrogatingList.add({ uuid: activeUuid }));
  } catch (error) {
    console.error(error);
  }
}

function* watchNavigateToVendorEvent(action) {
  const { payload } = action;
  const { stockType, vendorName } = payload;
  const activeUuid = yield select(getPartFileUuid);
  try {
    yield call(recordEventService.navigateToVendor, activeUuid, {
      stockType,
      vendorName,
    });
  } catch (error) {
    console.error(error);
  }
}
